export const auth = {
  activationPage: {
    loading: 'Activating account…',
  },
  footer: {
    social: {
      facebook: {
        ariaLabel: 'Facebook',
        link: 'https://www.facebook.com/creativeshelter/',
      },
      instagram: {
        ariaLabel: 'Instagram',
        link: 'https://www.instagram.com/creativeshelter/',
      },
      linkedIn: {
        ariaLabel: 'LinkedIn',
        link: 'https://be.linkedin.com/company/creative-shelter/',
      },
      website: {
        ariaLabel: 'Creative Shelter website',
        link: 'https://www.creativeshelter.be/',
      },
    },
    trademark: 'Creative Shelter © {{year}}',
  },
  forgotPasswordPage: {
    form: {
      submitButton: 'Send the link',
    },
    header: {
      subtitle: 'Enter your email address and we’ll send you the steps to set a new password.',
      title: 'Reset password',
    },
    loginCta: {
      link: 'Log in',
      text: 'Remember your password?',
    },
    successAlert: {
      description: 'The steps to set your new password are in your mailbox. Didn’t receive anything? Double-check your email address.',
      title: 'The steps are in your mailbox!',
    },
    tokenExpiredAlert: {
      description: 'Request a new link here to change your password.',
      title: 'Link expired',
    },
  },
  loginPage: {
    accountantLoginCta: {
      action: '<accountantLink href="https://api.creativeshelter.be/accountants/login">Log in here</accountantLink>',
      message: 'Are you an accountant?',
    },
    errors: {
      ineligible: {
        description:
          'You can’t log in yet because you’re not automatically eligible. We’re double-checking your profile and activities, you’ll hear from us soon!',
        title: 'Waiting for approval',
      },
      invalidEmailOrPassword: 'Your email address or password is incorrect.',
      rejected: {
        description:
          'We’ve checked your profile and you’re not eligible to invoice through Creative Shelter. Unfortunately, your account has therefore been deactivated.',
        title: 'Sorry, you’re not eligible',
      },
      unverifiedEmail: {
        description: 'Your email address hasn’t been verified yet. Check your mailbox and confirm your email address.',
        title: 'Waiting for verification',
      },
    },
    forgotPasswordLink: 'Forgot password?',
    form: {
      emailField: {
        label: 'Email address',
      },
      passwordField: {
        label: 'Password',
      },
      submitButton: 'Log in',
    },
    header: {
      eyebrow: 'Log in',
      subtitle: 'Log in to continue to the platform.',
      title: 'Welcome back!',
    },
    registerCta: {
      action: 'Create an account',
      message: 'Don’t have an account?',
    },
    sessionExpiredAlert: {
      description: 'Log in again to continue.',
      title: 'Session expired',
    },
  },
  logo: 'Creative Shelter website',
  passwordResetErrors: {
    ineligible: {
      description:
        'Your profile hasn’t been approved yet, so you can’t choose a new password yet. We’ll let you know as soon as your profile has been checked.',
      title: 'Waiting for approval',
    },
    rejected: {
      description:
        'We’ve checked your profile, but you’re not eligible to invoice through Creative Shelter. Your account has been deactivated for that reason.',
      title: 'Sorry, you’re not eligible',
    },
    unverifiedEmail: {
      description:
        'You can’t request a new password until your email address has been verified. Check your mailbox and verify your email address.',
      title: 'Waiting for verification',
    },
  },
  registrationPage: {
    common: {
      header: {
        pill: 'Registration',
      },
      loginCta: {
        action: 'Log in',
        message: 'Already have an account?',
      },
      previousStepButton: 'Previous',
      referralCode: {
        addButton: 'Discount code',
        applied: 'Discount!',
        form: {
          invitationsField: {
            description:
              'We see that several freelancers have invited you. Choose which invitation you want to accept, or enter a (different) discount code yourself.',
            invitedAt: 'Invited on {{date}}',
          },
          referralCodeField: {
            errors: {
              expired: 'This discount code has expired.',
              generic: 'The discount code cannot be validated at the moment.',
              invalid: 'This discount code is not valid.',
            },
            invitationDescription: 'You’ve been invited by {{name}} with discount code <code>{{code}}</code>.',
            label: 'Discount code',
          },
          submitButton: 'Apply',
        },
        types: {
          fromAccountant: 'The discount code {{referralCode}} from accountant {{name}} has been applied!',
          fromAdmin: {
            generic: 'The discount code {{referralCode}} has been applied!',
          },
          fromUser: 'A discount thanks to the code {{referralCode}} from {{name}}! Create your first invoice and you both get a discount!',
        },
      },
    },
    stepAccountInformation: {
      alertNoElibibleNacebelCodes: {
        description: 'You can continue your registration, but we’ll only activate your account after manually checking your activities.',
        title: 'You’re not eligible by default',
      },
      form: {
        callingCodeField: {
          label: 'Country code',
          options: {
            other: 'Other',
          },
        },
        emailField: {
          errors: {
            exists: 'This email address is already in use. Log in or reset your password.',
            generic:
              'Something went wrong while validating your email address. Contact <email href="mailto:service@creativeshelter.be">service@creativeshelter.be</email> if the problem persists.',
          },
          label: 'Email address',
        },
        passwordField: {
          label: 'Password',
          requirements: {
            error: 'The password doesn’t meet the requirements.',
            minLength: 'At least 8 characters',
            mixedCasing: 'At least 1 uppercase and 1 lowercase letter',
            nonAlpha: 'At least 1 number or special character',
          },
        },
        phoneNumberField: {
          label: 'Phone number',
        },
        submitButton: 'Next',
      },
      header: {
        subtitle: 'Ready to dive in?',
        title: 'Hi {{name}}',
      },
    },
    stepBusinessInformation: {
      errors: {
        fallback: 'An unexpected error occurred during your registration.',
        httpError:
          'Something went wrong while registering. Contact <email href="mailto:service@creativeshelter.be">service@creativeshelter.be</email> if the problem persists.',
      },
      form: {
        mainOccupationField: {
          label: 'Status',
          options: {
            mainOccupation: 'Main occupation',
            secondaryOccupation: 'Secondary occupation',
          },
        },
        otherActivitiesField: {
          label: 'Describe your other creative activities',
        },
        primaryActivityField: {
          label: 'Main activity',
        },
        secondaryActivityField: {
          label: 'Do you have any other creative activities?',
        },
        submitButton: 'Create an account',
      },
      header: {
        subtitle: 'Tell us more about your sole proprietorship.',
        title: 'Hi {{name}}',
      },
    },
    stepCompany: {
      alert: {
        description:
          'Our platform is made for sole proprietorships. Fortunately, Creative Shelter also offers a solution for companies. <break/>Interested? <kiiaLink href="https://www.creativeshelter.be/kom-ik-in-aanmerking">Check whether you’re eligible</kiiaLink> and we’ll get back to you soon.',
        title: 'Creative Shelter for companies',
      },
      changeVatNumber: {
        action: 'Change',
        message: 'Not the correct VAT number?',
      },
      header: {
        subtitle: 'We see that you have a company.',
        title: 'Hi, director of {{name}}',
      },
    },
    stepSuccess: {
      header: {
        subtitle: {
          eligible: 'You’ve reached the finish line, check your mailbox',
          ineligible: 'Details received!',
        },
        title: 'Your account has been created',
      },
      linkAccountant: {
        errors: {
          generic: 'Something went wrong while linking your accountant.',
        },
        form: {
          accountantEmailAddressField: {
            description:
              'You can enter your accountant’s email address here. If they don’t know Creative Shelter yet, we’ll briefly introduce ourselves.',
            label: 'Do you work with an accountant?',
          },
        },
        success: {
          message: 'Thanks for sending us your accountant’s details. We’ll get to work on it.',
          title: 'Received!',
        },
      },
      message: {
        eligible: 'An email has been sent to <bold>{{email}}</bold>. Confirm your email address via the link to activate your account.',
        ineligible:
          'We’re double-checking whether you’re eligible to invoice through Creative Shelter.<break/>You can close this window, we’ll let you know by email as soon as possible.',
      },
    },
    stepVatNumber: {
      alert: {
        kboNotAvailable: {
          description: 'Due to an outage at the CBE, we can’t check your VAT number right now. Please try again later.',
          title: 'The CBE is temporarily unavailable',
        },
      },
      form: {
        submitButton: 'Let’s go',
        vatNumberField: {
          description:
            'Don’t have a VAT number yet? <faqLink href="https://www.creativeshelter.be/knowledge-hub/kan-ik-mij-al-aansluiten-bij-creative-shelter-als-mijn-btw-nummer-nog-in-aanvraag-is">Read here</faqLink> what to do.',
          errors: {
            exists: 'There’s already an account with this VAT number. Could you double-check?',
            fallback: 'An unexpected error occurred while validating your VAT number.',
            httpError:
              'Something went wrong while validating your VAT number. Contact <email href="mailto:service@creativeshelter.be">service@creativeshelter.be</email> if the problem persists.',
            inactiveVatNumber:
              'Your VAT number isn’t active yet according to the CBE. You can only register with an active VAT number. Please try again later.',
            invalidVatNumber: 'The VAT number you entered doesn’t exist in the CBE. Could you double-check?',
            vatNumberChecksum: 'The VAT number doesn’t seem to be correct. Could you check whether there’s a mistake in it?',
            vatNumberLength: 'A VAT number consists of 10 digits, the first of which is 0 or 1.',
          },
          label: 'VAT number',
        },
      },
      header: {
        subtitle: 'Create your free Creative Shelter account',
        title: 'Get more out of your copyright',
      },
    },
  },
  resendVerificationEmail: {
    error: 'Something went wrong while sending the verification email. Contact service@creativeshelter.be if the problem persists.',
    send: 'Didn’t receive anything? <button>Click here</button> to resend the email.',
    sent: 'We’ve sent a new verification email to <bold>{{emailAddress}}</bold>. Still haven’t received anything? Try again after {{seconds}} seconds.',
  },
  resetPasswordPage: {
    form: {
      submitButton: 'Set your new password',
    },
    header: {
      subtitle: 'Choose a new password for your account.',
      title: 'Set a new password',
    },
  },
  sidebars: {
    checklist: {
      features: {
        first: 'Keep more net income thanks to your copyright.',
        second: 'Invoice smoothly through our platform.',
        third: 'Get help from real people and a freelance Community.',
        title: 'The only invoicing platform that takes care of copyright.',
      },
    },
    kiia: {
      eligibilityTest: {
        button: 'Do the check',
        description: 'Find out with a few simple questions whether you’re eligible for copyright income.',
        title: 'Can you invoice copyright?',
      },
    },
  },
};
